import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Activity, Bike,
  HeartPulse, Home, Menu, Settings, Utensils,
  Wrench, X, Database, ShieldCheck
} from "lucide-react";
import { Card } from "./components/ui/Card";
import {
  buildNavigationHash,
  readPersistedNavigationStateWithDiagnostics,
  resolveInitialNavigationState,
  writePersistedNavigationState,
  type AppPageId,
  type NavigationStateRecord,
  type RecoverySubview
} from "./app/navigationState";
import { AthleteCommandCenterPage } from "./features/command-center/AthleteCommandCenterPage";
import { NutritionIntelligence } from "./features/command-center/components/NutritionIntelligence";
import { RecoveryPlan } from "./features/command-center/components/RecoveryPlan";
import { GaragePage } from "./features/garage/GaragePage";
import { IntelligencePage } from "./features/intelligence/IntelligencePage";
import { TrainLandingPage } from "./features/train/TrainLandingPage";
import { RideAnalysisWorkspace } from "./features/train/RideAnalysisWorkspace";
import { TrainWorkflowErrorBoundary } from "./features/train/components/TrainWorkflowErrorBoundary";
import { AthleteTimelinePanel } from "./features/train/components/AthleteTimelinePanel";
import { BrowserPersistentActivityFileStorage } from "./features/train/activityFileStorage";
import { applyManualSourceLabel, classifyActivitySource, classifyParsedFitSource, createActivitySession, isDuplicateSource, matchActivityMetadata, recognizeSessionScreenshots, type ActivityMetadata, type ActivitySession, type ActivitySource, type ManualSourceLabel } from "./features/train/activitySources";
import { buildUnifiedActivityAnalysisInput, runUnifiedActivityAnalysis, type UnifiedActivityAnalysisResult } from "./features/train/sessionAnalysis";
import type { IntakeConflict } from "./features/train/components/ActivitySourceTray";
import {
  clearPersistedTrainActivityState,
  createPersistedTrainActivityState,
  type PersistedAnalysisStatus,
  hasTransientSources,
  initializeActivitySessionStore,
  isStableCompletedTrainState,
  loadPersistedTrainActivityState,
  loadPersistedTrainActivityStateBySessionId,
  getActivitySessionStoreStatus,
  restoreActivitySessionWithRecovery,
  savePersistedTrainActivityState,
  TRAIN_ACTIVITY_SESSION_SCHEMA_VERSION
} from "./features/train/activitySessionPersistence";
import { shouldApplyHydratedSnapshot, transitionHydrationLifecycle, type HydrationLifecycle } from "./features/train/hydrationLifecycle";
import { reduceTrainSubview, type TrainSubview } from "./features/train/trainViewState";
import {
  createCompletedActivityRecordFromSnapshot,
  deleteCompletedActivityHistoryRecord,
  listCompletedActivityHistoryRecords,
  saveCompletedActivityRecord,
  type CompletedActivityRecord,
  type DuplicateIdentityResult
} from "./features/train/completedActivityHistory";
import { buildPersonalBaselineResult, type PersonalBaselineResult } from "./features/train/personalBaseline";
import { buildAthleteIntelligenceSnapshot, type AthleteIntelligenceSnapshot } from "./features/train/athleteIntelligence";
import { createAthleteHistoryIndex } from "./features/history/athleteHistoryIndex";
import { HistoricalRideImportCancelledError, importHistoricalRideBatch, type HistoricalRideImportResult } from "./features/train/historicalRideImport";
import { epochNowMs } from "./features/train/workers/workerHandoffProfiling";
import type { HistoricalFitWorkerDiagnostics } from "./features/train/workers/historicalFitWorkerClient";
import type { HistoricalFitImportPerformanceDiagnostics } from "./features/train/workers/historicalFitWorkerProtocol";
import {
  buildAutomaticActivityStableSourceKey,
  createActivityCompletedTimelineEvent,
  createFtpChangeTimelineEvent,
  createFtpSnapshotContextEvent,
  createRaceTimelineEvent,
  createRideAnalysisContextEvent,
  createRideContextEvent,
  deleteAthleteTimelineEvent,
  listAthleteTimelineEvents,
  reconcileAthleteTimelineDuplicateAutomaticEvents,
  type AthleteTimelineEvent,
  upsertAthleteTimelineEvent,
  type AthleteTimelineEventType
} from "./features/train/athleteTimeline";
import { applyRideAnalytics, createFitParser } from "./features/ride-center/services/fitParser";
import { getSelectedUpload, runRideUploadPipeline, type RideParserDiagnostics, type RideUploadProgress } from "./features/ride-center/services/rideUploadPipeline";
import { createEmptyRide, type Ride } from "./features/ride-center/types/ride";
import { AthleteProfileProvider, useAthleteProfile } from "./features/athlete-profile/AthleteProfileProvider";
import { inferRideSurfaceType, selectFtpForRide, type FtpSelectionResult } from "./features/athlete-profile/ftpSelection";
import { AthleteProfileSettingsPage } from "./features/athlete-profile/components/AthleteProfileSettingsPage";
import { DailyHealthPanel } from "./features/health/components/DailyHealthPanel";
import { AppleHealthExportConnector } from "./features/health/healthDataConnector";
import { buildDailyHealthTimelineEvents, buildImportCompletedTimelineEvent } from "./features/health/healthTimelineIntegration";
import { listDailyHealthRecords, listHealthImportSessions } from "./features/health/dailyHealthPersistence";
import type { ConnectorImportResult } from "./features/health/healthDataConnector";
import type { DailyHealthRecord, HealthImportDiagnostics, HealthImportSession } from "./features/health/dailyHealthTypes";
import "./styles/globals.css";

function tracePersistence(message: string, details?: unknown): void {
  if (!import.meta.env.DEV) return;
  const timestamp = new Date().toISOString();
  if (details === undefined) {
    console.log(`[persistence ${timestamp}] ${message}`);
    return;
  }
  console.log(`[persistence ${timestamp}] ${message}`, details);
}

function tracePersistenceError(message: string, error: unknown): void {
  if (!import.meta.env.DEV) return;
  console.error(`[persistence ${new Date().toISOString()}] ${message}`, error);
}

type NavItem = {
  id: AppPageId;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
};

const navItems: NavItem[] = [
  { id: "home", label: "Home", icon: Home },
  { id: "train", label: "Train", icon: Bike },
  { id: "nutrition", label: "Nutrition", icon: Utensils },
  { id: "recovery", label: "Recovery", icon: HeartPulse },
  { id: "equipment", label: "Equipment", icon: Wrench },
  { id: "intelligence", label: "Intelligence", icon: Activity },
  { id: "settings", label: "Settings", icon: Settings }
];

type NavigationDiagnostics = {
  lastActivePage: AppPageId;
  restoredPage: AppPageId;
  restorationSource: "url" | "storage" | "default";
  fallbackReason: string | null;
  activeSubview: string;
  explicitUrlRouteTookPrecedence: boolean;
};

type HistoricalImportRuntimeDiagnostics = {
  fileName: string | null;
  fileSizeBytes: number | null;
  workerStartedAt: string | null;
  currentStage: string | null;
  stageStartedAt: string | null;
  lastWorkerMessageAt: string | null;
  workerState: string | null;
  workerActive: boolean;
  cancellationAvailable: boolean;
  performanceDiagnostics?: HistoricalFitImportPerformanceDiagnostics | null;
};

function createEmptyRideSummary(): Ride {
  return createEmptyRide();
}

type PersistenceDiagnostics = {
  storageBackend: string;
  databaseOpenStatus: string;
  currentSessionId: string | null;
  saveAttempted: boolean;
  saveResult: "idle" | "skipped" | "success" | "failed";
  lastSavedTimestamp: string | null;
  schemaVersion: number;
  approximateStoredSizeBytes: number | null;
  hydrationLifecycle: HydrationLifecycle;
  restoreAttempted: boolean;
  restoreResult: "idle" | "not_found" | "success" | "failed";
  restoredParsedRidePresent: boolean;
  restoredCanonicalRidePresent: boolean;
  analysisResultPresentLive: boolean;
  analysisResultIncludedInSave: boolean;
  analysisSaveAttempted: boolean;
  analysisSaveResult: "idle" | "success" | "failed";
  restoredAnalysisResultPresent: boolean;
  restoredAnalysisStatus: PersistedAnalysisStatus;
  restoredTrainSubview: TrainSubview;
  analysisStale: boolean;
  lastPersistenceError: string | null;
  historyPersistenceStatus?: string;
  historyRecordCount?: number;
  historyDuplicateIdentity?: DuplicateIdentityResult | null;
};

type PendingPersistenceSave = {
  activitySession: ActivitySession;
  parsedRide: Ride;
  unifiedAnalysis: UnifiedActivityAnalysisResult | null;
  lastFtpSelection: FtpSelectionResult | null;
  analysisFtpSelection: FtpSelectionResult | null;
  parserDiagnostics: RideParserDiagnostics | null;
  trainSubview: TrainSubview;
  selectedIntervalId: string | null;
  analysisStatus: PersistedAnalysisStatus;
  analysisCompletedAt: string | null;
  reason: string;
};

type TimelineDiagnostics = {
  totalEvents: number;
  automaticEventsCreated: number;
  duplicateEventsPrevented: number;
  latestEventDate: string | null;
  saveResult: string;
  restoreResult: string;
  sourceLinkageStatus: string;
  lastSubmitAttempted: string | null;
  lastSubmitResult: "idle" | "saving" | "success" | "failed";
  lastValidationError: string | null;
  lastPersistenceError: string | null;
  generatedEventId: string | null;
  generatedStableSourceKey: string | null;
  timelineCountBeforeSave: number | null;
  timelineCountAfterSave: number | null;
  duplicateCandidatesFound: number;
  duplicateEventsReconciled: number;
};

function isTerminalHydrationLifecycle(lifecycle: HydrationLifecycle): boolean {
  return lifecycle === "hydrated" || lifecycle === "failed";
}

function hasRestorableUnifiedAnalysis(value: unknown): value is UnifiedActivityAnalysisResult {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.sessionOverview === "object"
    && typeof candidate.analysisConfidence === "object"
    && typeof candidate.rideClassification === "object";
}

function emptyAthleteIntelligenceSnapshot(): AthleteIntelligenceSnapshot {
  return {
    rideCount: 0,
    lastAnalysisDate: null,
    historicalMetrics: {
      averageIf: null,
      averageNp: null,
      averagePower: null,
      averageDecoupling: null,
      averageDurability: null,
      averageVariabilityIndex: null,
      averageRespiratoryDrift: null,
      averageRideConfidence: null
    },
    rollingAverages: {
      last7Rides: {},
      last30Days: {},
      last90Days: {}
    },
    currentTrend: "stable",
    longTermTrend: "stable",
    highestConfidenceMetrics: [],
    comparisons: []
  };
}

export function App() {
  const { athleteProfile } = useAthleteProfile();
  const initialNavigation = useMemo(() => {
    // Deterministic startup: resolve based ONLY on explicit URL hash.
    // Never restore persisted navigation from localStorage on initialization.
    return resolveInitialNavigationState({
      hash: typeof window !== "undefined" ? window.location.hash : ""
    });
  }, []);
  const [active, setActive] = useState<AppPageId>(initialNavigation.state.activePage);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activitySession, setActivitySession] = useState<ActivitySession>(() => createActivitySession());
  const [intakeConflict, setIntakeConflict] = useState<IntakeConflict | null>(null);
  const [pendingPrimaryCandidate, setPendingPrimaryCandidate] = useState<{ source: ActivitySource; ride: Ride; diagnostics: RideParserDiagnostics; ftpSelection: FtpSelectionResult } | null>(null);
  const [analysisStage, setAnalysisStage] = useState<string | null>(null);
  const [unifiedAnalysis, setUnifiedAnalysis] = useState<UnifiedActivityAnalysisResult | null>(null);
  const [analysisStatus, setAnalysisStatus] = useState<PersistedAnalysisStatus>("idle");
  const [analysisCompletedAt, setAnalysisCompletedAt] = useState<string | null>(null);
  const [restoredAnalysisMessage, setRestoredAnalysisMessage] = useState<string | null>(null);
  const [trainSubview, setTrainSubview] = useState<TrainSubview>(initialNavigation.state.trainSubview);
  const [parsedRide, setParsedRide] = useState<Ride | null>(null);
  const [isParsingRide, setIsParsingRide] = useState(false);
  const [rideParseError, setRideParseError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<RideUploadProgress | null>(null);
  const [parserDiagnostics, setParserDiagnostics] = useState<RideParserDiagnostics | null>(null);
  const [selectedIntervalId, setSelectedIntervalId] = useState<string | null>(null);
  const [lastFtpSelection, setLastFtpSelection] = useState<FtpSelectionResult | null>(null);
  const [analysisFtpSelection, setAnalysisFtpSelection] = useState<FtpSelectionResult | null>(null);
  const [persistenceReady, setPersistenceReady] = useState(false);
  const [restoredFromBrowser, setRestoredFromBrowser] = useState(false);
  const [hydrationLifecycle, setHydrationLifecycle] = useState<HydrationLifecycle>("not_started");
  const [lastTrainAction, setLastTrainAction] = useState("INIT");
  const [lastPersistenceWriteState, setLastPersistenceWriteState] = useState("idle");
  const [lastCaughtError, setLastCaughtError] = useState<string | null>(null);
  const [restoreFailureMessage, setRestoreFailureMessage] = useState<string | null>(null);
  const [activityHistoryRecords, setActivityHistoryRecords] = useState<CompletedActivityRecord[]>([]);
  const [baselineResult, setBaselineResult] = useState<PersonalBaselineResult | null>(null);
  const [historyPersistenceStatus, setHistoryPersistenceStatus] = useState("idle");
  const [historyDuplicateIdentity, setHistoryDuplicateIdentity] = useState<DuplicateIdentityResult | null>(null);
  const [historicalImportResult, setHistoricalImportResult] = useState<HistoricalRideImportResult | null>(null);
  const [historicalImportInProgress, setHistoricalImportInProgress] = useState(false);
  const [historicalImportProgressPercent, setHistoricalImportProgressPercent] = useState(0);
  const [historicalImportProgressStage, setHistoricalImportProgressStage] = useState<string | null>(null);
  const [historicalImportProgressStatus, setHistoricalImportProgressStatus] = useState<string | null>(null);
  const [historicalImportWorkerDiagnostics, setHistoricalImportWorkerDiagnostics] = useState<HistoricalFitWorkerDiagnostics | null>(null);
  const [historicalImportRuntimeDiagnostics, setHistoricalImportRuntimeDiagnostics] = useState<HistoricalImportRuntimeDiagnostics | null>(null);
  const [athleteTimelineEvents, setAthleteTimelineEvents] = useState<AthleteTimelineEvent[]>([]);
  const [dailyHealthRecords, setDailyHealthRecords] = useState<DailyHealthRecord[]>([]);
  const [healthImportSessions, setHealthImportSessions] = useState<HealthImportSession[]>([]);
  const [dailyHealthDiagnostics, setDailyHealthDiagnostics] = useState<HealthImportDiagnostics | null>(null);
  const [selectedDailyHealthDate, setSelectedDailyHealthDate] = useState<string | null>(initialNavigation.state.selectedDailyHealthDate);
  const [recoverySubview, setRecoverySubview] = useState<RecoverySubview>(initialNavigation.state.recoverySubview);
  const [navigationDiagnostics, setNavigationDiagnostics] = useState<NavigationDiagnostics>({
    lastActivePage: initialNavigation.state.activePage,
    restoredPage: initialNavigation.restoredPage,
    restorationSource: initialNavigation.restorationSource,
    fallbackReason: initialNavigation.fallbackReason,
    activeSubview: initialNavigation.state.activePage === "train"
      ? initialNavigation.state.trainSubview
      : initialNavigation.state.activePage === "recovery"
        ? initialNavigation.state.recoverySubview
        : "none",
    explicitUrlRouteTookPrecedence: initialNavigation.explicitUrlRouteTookPrecedence
  });
  const [timelineDiagnostics, setTimelineDiagnostics] = useState<TimelineDiagnostics>({
    totalEvents: 0,
    automaticEventsCreated: 0,
    duplicateEventsPrevented: 0,
    latestEventDate: null,
    saveResult: "idle",
    restoreResult: "idle",
    sourceLinkageStatus: "unknown",
    lastSubmitAttempted: null,
    lastSubmitResult: "idle",
    lastValidationError: null,
    lastPersistenceError: null,
    generatedEventId: null,
    generatedStableSourceKey: null,
    timelineCountBeforeSave: null,
    timelineCountAfterSave: null,
    duplicateCandidatesFound: 0,
    duplicateEventsReconciled: 0
  });
  const [persistenceDiagnostics, setPersistenceDiagnostics] = useState<PersistenceDiagnostics>({
    storageBackend: "indexeddb",
    databaseOpenStatus: "unknown",
    currentSessionId: null,
    saveAttempted: false,
    saveResult: "idle",
    lastSavedTimestamp: null,
    schemaVersion: TRAIN_ACTIVITY_SESSION_SCHEMA_VERSION,
    approximateStoredSizeBytes: null,
    hydrationLifecycle: "not_started",
    restoreAttempted: false,
    restoreResult: "idle",
    restoredParsedRidePresent: false,
    restoredCanonicalRidePresent: false,
    analysisResultPresentLive: false,
    analysisResultIncludedInSave: false,
    analysisSaveAttempted: false,
    analysisSaveResult: "idle",
    restoredAnalysisResultPresent: false,
    restoredAnalysisStatus: "idle",
    restoredTrainSubview: "overview",
    analysisStale: false,
    lastPersistenceError: null
  });
  const liveTrainMutationRef = useRef(false);
  const pendingPersistenceSaveRef = useRef<PendingPersistenceSave | null>(null);
  const lastHistoryUpdateKeyRef = useRef<string | null>(null);
  const lastTimelineActivityUpdateKeyRef = useRef<string | null>(null);
  const previousFtpWattsRef = useRef<number | null>(null);
  const historicalImportAbortRef = useRef<AbortController | null>(null);
  const fitParser = useMemo(() => createFitParser(), []);
  const activityFileStorage = useMemo(() => new BrowserPersistentActivityFileStorage(), []);
  const appleHealthConnector = useMemo(() => new AppleHealthExportConnector(), []);
  const emptyRide = useMemo(() => createEmptyRideSummary(), []);
  const athleteHistoryIndex = useMemo(() => createAthleteHistoryIndex({
    rides: activityHistoryRecords,
    dailyHealth: dailyHealthRecords,
    ftpHistory: athleteProfile.performance.ftpHistory,
    timelineEvents: athleteTimelineEvents
  }), [activityHistoryRecords, dailyHealthRecords, athleteProfile.performance.ftpHistory, athleteTimelineEvents]);

  const historyDateRange = useMemo(() => athleteHistoryIndex.getAvailableDateRange(), [athleteHistoryIndex]);
  const historySourceSummary = useMemo(() => athleteHistoryIndex.getSourceCoverageSummary(), [athleteHistoryIndex]);

  const athleteIntelligence = useMemo(() => {
    const range = athleteHistoryIndex.getAvailableDateRange().rides;
    const indexedRides = range.start && range.end
      ? athleteHistoryIndex.getRidesBetween(range.start, range.end)
      : [];
    if (indexedRides.length === 0) {
      return emptyAthleteIntelligenceSnapshot();
    }
    return buildAthleteIntelligenceSnapshot(indexedRides);
  }, [athleteHistoryIndex]);

  useEffect(() => {
    return () => {
      historicalImportAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const onBeforeUnload = () => {
      historicalImportAbortRef.current?.abort();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  useEffect(() => {
    tracePersistence("PersistenceProvider mounted");
  }, []);

  useEffect(() => {
    const nextState: NavigationStateRecord = {
      version: 1,
      activePage: active,
      trainSubview,
      recoverySubview,
      selectedDailyHealthDate,
      updatedAt: new Date().toISOString()
    };

    writePersistedNavigationState(nextState);

    if (typeof window !== "undefined") {
      const nextHash = buildNavigationHash(nextState);
      if (window.location.hash !== nextHash) {
        window.history.replaceState(window.history.state, "", nextHash);
      }
    }

    setNavigationDiagnostics(current => ({
      ...current,
      lastActivePage: active,
      activeSubview: active === "train" ? trainSubview : active === "recovery" ? recoverySubview : "none"
    }));
  }, [active, trainSubview, recoverySubview, selectedDailyHealthDate]);

  function markTrainMutation(action: string) {
    liveTrainMutationRef.current = true;
    setLastTrainAction(action);
  }

  function getRideMetadata(ride: Ride): ActivityMetadata {
    return {
      startTime: ride.rideDate ? new Date(ride.rideDate).getTime() : null,
      durationSeconds: ride.elapsedTime ?? ride.movingTime ?? null,
      distanceMeters: ride.distance ?? null
    };
  }

  function ensureSessionId(session: ActivitySession): ActivitySession {
    if (session.id && session.id.trim().length > 0) {
      return session;
    }
    return {
      ...session,
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`
    };
  }

  function markAnalysisStaleIfComplete(): void {
    setAnalysisStatus(current => current === "complete" ? "stale" : current);
    setRestoredAnalysisMessage(null);
    setPersistenceDiagnostics(current => ({
      ...current,
      analysisStale: current.analysisResultPresentLive
    }));
  }

  function buildCurrentCompletedActivityRecordSnapshot(input: {
    ride: Ride;
    analysis: UnifiedActivityAnalysisResult;
    ftpSelection: FtpSelectionResult | null;
    completedAt: string | null;
  }): CompletedActivityRecord {
    return createCompletedActivityRecordFromSnapshot({
      activityId: activitySession.id,
      activitySession,
      parsedRide: input.ride,
      analysis: input.analysis,
      analysisFtpSelection: input.ftpSelection,
      completedAt: input.completedAt
    });
  }

  async function refreshCompletedActivityHistory(): Promise<void> {
    const records = await listCompletedActivityHistoryRecords();
    setActivityHistoryRecords(records);
    setPersistenceDiagnostics(current => ({
      ...current,
      historyRecordCount: records.length
    }));
  }

  async function refreshAthleteTimeline(reason: string): Promise<void> {
    const reconciliation = await reconcileAthleteTimelineDuplicateAutomaticEvents();
    const events = await listAthleteTimelineEvents();
    setAthleteTimelineEvents(events);
    setTimelineDiagnostics(current => ({
      ...current,
      totalEvents: events.length,
      latestEventDate: events[0]?.eventDate ?? null,
      sourceLinkageStatus: events.some(event => event.source === "automatic_completed_activity" && event.sourceId)
        ? "linked"
        : events.length > 0
          ? "partial"
          : "none",
      restoreResult: reason,
      duplicateCandidatesFound: reconciliation.duplicateCandidatesFound,
      duplicateEventsReconciled: reconciliation.duplicateEventsReconciled
    }));
  }

  async function refreshDailyHealthState(): Promise<void> {
    const [records, sessions] = await Promise.all([
      listDailyHealthRecords(),
      listHealthImportSessions()
    ]);
    setDailyHealthRecords(records);
    setHealthImportSessions(sessions);
    if (!selectedDailyHealthDate && records.length > 0) {
      setSelectedDailyHealthDate(records[0].localDate);
    }
  }

  async function handleDailyHealthImportCompleted(result: ConnectorImportResult): Promise<void> {
    setDailyHealthDiagnostics(result.diagnostics);
    setDailyHealthRecords(result.dailyRecords);
    const sessions = await listHealthImportSessions();
    setHealthImportSessions(sessions);

    let timelineEventsCreated = 0;
    const importEvent = buildImportCompletedTimelineEvent(result.session);
    const importUpsert = await upsertAthleteTimelineEvent(importEvent);
    if (importUpsert.action === "created") {
      timelineEventsCreated += 1;
    }

    const meaningfulEvents = result.dailyRecords.flatMap(record => buildDailyHealthTimelineEvents(record));
    for (const eventInput of meaningfulEvents) {
      const upsert = await upsertAthleteTimelineEvent(eventInput);
      if (upsert.action === "created") {
        timelineEventsCreated += 1;
      }
    }

    setDailyHealthDiagnostics(current => current
      ? { ...current, timelineEventsCreated }
      : current);
    await refreshAthleteTimeline("daily-health-import");
  }

  function handleDailyHealthImportError(message: string): void {
    setDailyHealthDiagnostics(current => current
      ? { ...current, errors: [...current.errors, message] }
      : null);
  }

  async function saveTimelineManualEvent(input: {
    id?: string;
    eventType: AthleteTimelineEventType;
    eventDate: string;
    title: string;
    summary: string;
    notes?: string;
    tags: string[];
    metrics?: Record<string, number>;
  }): Promise<{
    ok: boolean;
    message: string;
    event?: AthleteTimelineEvent;
    diagnostics?: {
      generatedEventId: string | null;
      generatedStableSourceKey: string | null;
      timelineCountBeforeSave: number | null;
      timelineCountAfterSave: number | null;
      lastPersistenceError: string | null;
    };
  }> {
    const timelineCountBeforeSave = (await listAthleteTimelineEvents()).length;
    const attemptedAt = new Date().toISOString();
    setTimelineDiagnostics(current => ({
      ...current,
      lastSubmitAttempted: attemptedAt,
      lastSubmitResult: "saving",
      lastValidationError: null,
      lastPersistenceError: null,
      timelineCountBeforeSave
    }));

    try {
      const result = await upsertAthleteTimelineEvent({
        id: input.id,
        eventDate: input.eventDate,
        eventType: input.eventType,
        title: input.title,
        summary: input.summary,
        source: "manual_entry",
        sourceId: input.id,
        tags: input.tags,
        metrics: input.metrics,
        notes: input.notes,
        confidence: "context_only"
      });

      await refreshAthleteTimeline("manual-save");
      const timelineCountAfterSave = (await listAthleteTimelineEvents()).length;
      setTimelineDiagnostics(current => ({
        ...current,
        saveResult: `${result.action}:${result.backend}:${result.databaseStatus}`,
        duplicateEventsPrevented: current.duplicateEventsPrevented + (result.action === "unchanged" ? 1 : 0),
        lastSubmitResult: "success",
        generatedEventId: result.event.id,
        generatedStableSourceKey: result.stableSourceKey,
        timelineCountAfterSave,
        lastPersistenceError: null
      }));

      return {
        ok: true,
        message: input.id ? "Timeline event updated" : "Timeline event added",
        event: result.event,
        diagnostics: {
          generatedEventId: result.event.id,
          generatedStableSourceKey: result.stableSourceKey,
          timelineCountBeforeSave,
          timelineCountAfterSave,
          lastPersistenceError: null
        }
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to persist timeline event.";
      setTimelineDiagnostics(current => ({
        ...current,
        saveResult: `failed:${message}`,
        lastSubmitResult: "failed",
        lastPersistenceError: message,
        timelineCountAfterSave: current.totalEvents
      }));
      return {
        ok: false,
        message,
        diagnostics: {
          generatedEventId: null,
          generatedStableSourceKey: null,
          timelineCountBeforeSave,
          timelineCountAfterSave: null,
          lastPersistenceError: message
        }
      };
    }
  }

  async function deleteTimelineEvent(id: string): Promise<void> {
    try {
      await deleteAthleteTimelineEvent(id);
      setTimelineDiagnostics(current => ({
        ...current,
        saveResult: "deleted",
        lastPersistenceError: null
      }));
      await refreshAthleteTimeline("manual-delete");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to delete timeline event.";
      setTimelineDiagnostics(current => ({
        ...current,
        saveResult: `failed:${message}`,
        lastPersistenceError: message
      }));
    }
  }

  async function persistCompletedActivityHistory(input: {
    ride: Ride;
    analysis: UnifiedActivityAnalysisResult;
    ftpSelection: FtpSelectionResult | null;
    completedAt: string | null;
  }): Promise<void> {
    const candidateRecord = buildCurrentCompletedActivityRecordSnapshot(input);
    const dedupeKey = `${candidateRecord.activityId}:${candidateRecord.analysisVersion}:${candidateRecord.duplicateIdentity.stableSourceHash}:${candidateRecord.updatedAt.slice(0, 16)}`;
    if (lastHistoryUpdateKeyRef.current === dedupeKey) {
      return;
    }
    lastHistoryUpdateKeyRef.current = dedupeKey;
    setHistoryPersistenceStatus("saving");
    const saveResult = await saveCompletedActivityRecord(candidateRecord);
    setHistoryPersistenceStatus(`saved:${saveResult.backend}:${saveResult.databaseStatus}`);
    setHistoryDuplicateIdentity(saveResult.duplicateIdentity);
    setPersistenceDiagnostics(current => ({
      ...current,
      historyPersistenceStatus: `saved:${saveResult.backend}:${saveResult.databaseStatus}`,
      historyDuplicateIdentity: saveResult.duplicateIdentity
    }));
    await refreshCompletedActivityHistory();
  }

  async function openHistoryAnalysis(activityId: string): Promise<void> {
    const restored = await loadPersistedTrainActivityStateBySessionId(activityId);
    if (!restored.state || !restored.state.unifiedAnalysis) {
      setRestoreFailureMessage("This historical analysis snapshot is no longer available.");
      return;
    }

    const restoredSession = await restoreActivitySessionWithRecovery(restored.state.activitySession, activityFileStorage);
    setActivitySession(restoredSession.session);
    setParsedRide(restored.state.parsedRide);
    setUnifiedAnalysis(restored.state.unifiedAnalysis);
    setAnalysisStatus(restored.state.analysisStatus ?? "complete");
    setAnalysisCompletedAt(restored.state.analysisCompletedAt ?? null);
    setLastFtpSelection(restored.state.lastFtpSelection);
    setAnalysisFtpSelection(restored.state.analysisFtpSelection);
    setParserDiagnostics(restored.state.parserDiagnostics);
    setSelectedIntervalId(restored.state.selectedIntervalId);
    setTrainSubview("analysis");
    setActive("train");
    setRestoredAnalysisMessage("Completed analysis restored from activity history");
  }

  async function deleteHistoryActivity(activityId: string): Promise<void> {
    await deleteCompletedActivityHistoryRecord(activityId);
    await refreshCompletedActivityHistory();
  }

  function getRideSurfaceTypeForSelection(): "road" | "gravel" | "unknown" {
    const sourceName = parsedRide?.sourceFileName ?? activitySession.primary?.file.name ?? "";
    return inferRideSurfaceType(sourceName);
  }

  function getCurrentFtpSelection() {
    return selectFtpForRide(athleteProfile, getRideSurfaceTypeForSelection(), lastFtpSelection?.selectedPolicy ?? null);
  }

  function handleFtpSelectionChange(selection: FtpSelectionResult) {
    setLastFtpSelection(selection);
    markAnalysisStaleIfComplete();
  }

  async function persistCompletedSession(state: {
    activitySession: ActivitySession;
    parsedRide: Ride;
    unifiedAnalysis: UnifiedActivityAnalysisResult | null;
    lastFtpSelection: FtpSelectionResult | null;
    analysisFtpSelection: FtpSelectionResult | null;
    parserDiagnostics: RideParserDiagnostics | null;
    trainSubview: TrainSubview;
    selectedIntervalId: string | null;
    analysisStatus: PersistedAnalysisStatus;
    analysisCompletedAt: string | null;
    reason: string;
  }): Promise<void> {
    if (!isTerminalHydrationLifecycle(hydrationLifecycle)) {
      tracePersistence("save returned early", {
        reason: `hydration lifecycle is ${hydrationLifecycle}`,
        saveReason: state.reason
      });
      pendingPersistenceSaveRef.current = {
        activitySession: ensureSessionId(state.activitySession),
        parsedRide: state.parsedRide,
        unifiedAnalysis: state.unifiedAnalysis,
        lastFtpSelection: state.lastFtpSelection,
        analysisFtpSelection: state.analysisFtpSelection,
        parserDiagnostics: state.parserDiagnostics,
        trainSubview: state.trainSubview,
        selectedIntervalId: state.selectedIntervalId,
        analysisStatus: state.analysisStatus,
        analysisCompletedAt: state.analysisCompletedAt,
        reason: state.reason
      };
      setLastPersistenceWriteState(`skipped: hydration-${hydrationLifecycle}`);
      setPersistenceDiagnostics(current => ({
        ...current,
        saveAttempted: true,
        saveResult: "skipped",
        hydrationLifecycle
      }));
      return;
    }

    const nextSession = ensureSessionId(state.activitySession);
    if (!isStableCompletedTrainState({ activitySession: nextSession, parsedRide: state.parsedRide, isParsingRide: false })) {
      tracePersistence("save returned early", {
        reason: "train state is not stable and complete",
        saveReason: state.reason,
        sessionId: nextSession.id,
        primaryStatus: nextSession.primary?.status ?? null,
        parsedRideRecords: state.parsedRide.records.length,
        canonicalRidePresent: nextSession.activityMetadata !== null
      });
      setLastPersistenceWriteState(`skipped: incomplete-state:${state.reason}`);
      setPersistenceDiagnostics(current => ({
        ...current,
        saveAttempted: true,
        saveResult: "skipped",
        hydrationLifecycle
      }));
      return;
    }

    tracePersistence("save begin", { reason: state.reason, sessionId: nextSession.id });
    tracePersistence("awaiting Promise: savePersistedTrainActivityState", { sessionId: nextSession.id });
    const saveResult = await savePersistedTrainActivityState(createPersistedTrainActivityState({
      activitySession: nextSession,
      parsedRide: state.parsedRide,
      unifiedAnalysis: state.unifiedAnalysis,
      analysisStatus: state.analysisStatus,
      analysisCompletedAt: state.analysisCompletedAt,
      lastFtpSelection: state.lastFtpSelection,
      analysisFtpSelection: state.analysisFtpSelection,
      parserDiagnostics: state.parserDiagnostics,
      trainSubview: state.trainSubview,
      selectedIntervalId: state.selectedIntervalId,
      canAnalyze: true
    }));
    tracePersistence("Promise resolved: savePersistedTrainActivityState", { sessionId: saveResult.sessionId });
    tracePersistence("save complete", { sessionId: saveResult.sessionId, savedAt: saveResult.savedAt });
    setLastPersistenceWriteState("saved");
    setPersistenceDiagnostics(current => ({
      ...current,
      storageBackend: saveResult.backend,
      databaseOpenStatus: saveResult.databaseStatus,
      currentSessionId: saveResult.sessionId,
      saveAttempted: true,
      saveResult: "success",
      lastSavedTimestamp: saveResult.savedAt,
      schemaVersion: saveResult.schemaVersion,
      approximateStoredSizeBytes: saveResult.approximateSizeBytes,
      analysisResultPresentLive: state.unifiedAnalysis !== null,
      analysisResultIncludedInSave: state.unifiedAnalysis !== null,
      hydrationLifecycle,
      lastPersistenceError: null
    }));
  }

  function selectPage(id: AppPageId) {
    setNavigationDiagnostics(current => ({
      ...current,
      lastActivePage: id,
      activeSubview: id === "train" ? trainSubview : id === "recovery" ? recoverySubview : "none"
    }));
    if (id === "train" && active !== "train") {
      setTrainSubview("overview");
    }
    if (id === "recovery") {
      setRecoverySubview("daily-health");
    }
    setActive(id);
    setMenuOpen(false);
  }

  async function parseSelectedRide(file: File, source: ActivitySource) {
    setLastTrainAction("PRIMARY_PARSE_STARTED");
    setIsParsingRide(true);
    setRideParseError(null);
    setParserDiagnostics(null);
    setLastCaughtError(null);
    setActivitySession(current => ({
      ...current,
      primary: current.primary?.id === source.id
        ? { ...current.primary, status: "validating", note: "Validating and parsing primary FIT" }
        : current.primary
    }));

    try {
      const rideSurfaceType = inferRideSurfaceType(file.name);
      const ftpSelection = selectFtpForRide(athleteProfile, rideSurfaceType, lastFtpSelection?.selectedPolicy ?? null);
      const result = await runRideUploadPipeline(file, fitParser, ftpSelection.ftpWatts, setUploadProgress, { runAnalysis: false });

      setLastTrainAction("PRIMARY_PARSE_SUCCEEDED");
      tracePersistence("PRIMARY_PARSE_SUCCEEDED received", {
        sourceId: source.id,
        records: result.ride.records.length
      });
      setLastFtpSelection(ftpSelection);
      setParsedRide(result.ride);
      tracePersistence("parsed ride stored", { sourceId: source.id, records: result.ride.records.length });
      setRideParseError(null);
      setUploadProgress({ stage: "complete", label: "Primary ride parsed", progress: 100 });
      setParserDiagnostics(result.diagnostics);
      setIsParsingRide(false);
      setRestoredAnalysisMessage(null);
      const parsedPrimary = classifyParsedFitSource(file, result.ride, result.diagnostics);
      const parsedPrimaryNote = parsedPrimary.note;
      const parsedPrimaryMetadata = getRideMetadata(result.ride);
      const primaryRange = parsedPrimaryMetadata.startTime !== null && parsedPrimaryMetadata.durationSeconds !== null
        ? {
            start: parsedPrimaryMetadata.startTime,
            end: parsedPrimaryMetadata.startTime + parsedPrimaryMetadata.durationSeconds * 1000
          }
        : undefined;
      const alignedCompanionSources = primaryRange
        ? await Promise.all(activitySession.companionSources.map(async candidate => {
          if (candidate.role !== "companion") return candidate;
          const refreshed = await classifyActivitySource(candidate.file, candidate.role, primaryRange);
          return {
            ...refreshed,
            id: candidate.id,
            fingerprint: candidate.fingerprint,
            previewUrl: candidate.previewUrl,
            confirmedScreenshotValues: candidate.confirmedScreenshotValues
          };
        }))
        : activitySession.companionSources;
      const nextSessionForSave = ensureSessionId({
        ...activitySession,
        primary: {
          ...source,
          type: parsedPrimary.sourceType,
          confidence: parsedPrimary.confidence,
          fitDiagnostics: parsedPrimary.fitDiagnostics,
          status: "parsed",
          note: parsedPrimaryNote
        },
        activityMetadata: parsedPrimaryMetadata,
        companionSources: recognizeSessionScreenshots(alignedCompanionSources)
      });
      setActivitySession(current => {
        if (current.primary?.id !== source.id) {
          return current;
        }
        const nextSession = ensureSessionId({
          ...current,
          primary: {
            ...current.primary,
            type: parsedPrimary.sourceType,
            confidence: parsedPrimary.confidence,
            fitDiagnostics: parsedPrimary.fitDiagnostics,
            status: "parsed",
            note: parsedPrimaryNote
          },
          activityMetadata: parsedPrimaryMetadata,
          companionSources: recognizeSessionScreenshots(alignedCompanionSources)
        });
        tracePersistence("canonical ride stored", {
          sessionId: nextSession.id,
          canonicalRidePresent: nextSession.activityMetadata !== null
        });
        return nextSession;
      });
      try {
        await persistCompletedSession({
          activitySession: nextSessionForSave,
          parsedRide: result.ride,
          unifiedAnalysis,
          lastFtpSelection: ftpSelection,
          analysisFtpSelection: null,
          parserDiagnostics: result.diagnostics,
          trainSubview,
          selectedIntervalId,
          analysisStatus,
          analysisCompletedAt,
          reason: "PRIMARY_PARSE_SUCCEEDED"
        });
      } catch (error) {
        tracePersistenceError("Promise rejected: persistCompletedSession", error);
        const message = error instanceof Error ? error.message : "Unable to persist activity state.";
        setLastCaughtError(message);
        setLastPersistenceWriteState(`failed: ${message}`);
        setPersistenceDiagnostics(current => ({
          ...current,
          saveAttempted: true,
          saveResult: "failed",
          lastPersistenceError: message
        }));
      }
    } catch (error) {
      tracePersistenceError("Promise rejected: primary parse pipeline", error);
      setLastFtpSelection(null);
      setParsedRide(null);
      const message = error instanceof Error ? error.message : "Unable to decode the selected FIT file.";
      setLastTrainAction("PRIMARY_PARSE_FAILED");
      setLastCaughtError(message);
      setRideParseError(message);
      setUploadProgress({ stage: "failed", label: "Upload failed", progress: 100 });
      setParserDiagnostics(null);
      setIsParsingRide(false);
      setActivitySession(current => ({
        ...current,
        primary: current.primary?.id === source.id ? { ...current.primary, status: "failed", note: message } : current.primary
      }));
    }
  }

  async function handlePrimarySelection(files: FileList | null) {
    markTrainMutation("PRIMARY_FILE_SELECTED");
    const selectedFile = getSelectedUpload(files);
    if (!selectedFile) {
      return;
    }
    await handlePrimaryFile(selectedFile);
  }

  async function handlePrimaryFile(selectedFile: File) {
    markTrainMutation("PRIMARY_SOURCE_CREATED");
    markAnalysisStaleIfComplete();
    try {
      const classifiedSource = await classifyActivitySource(selectedFile, "primary");
      if (classifiedSource.status === "unsupported") {
        const message = classifiedSource.note ?? "Primary ride must be a .fit or .fit.gz file.";
        setLastTrainAction("PRIMARY_PARSE_FAILED");
        setLastCaughtError(message);
        setRideParseError(message);
        setUploadProgress({ stage: "failed", label: "Unsupported primary ride", progress: 100 });
        return;
      }
      const source = { ...classifiedSource, status: "validating" as const };
      const existingSources = [activitySession.primary, ...activitySession.supportingRideSources, ...activitySession.companionSources].filter((item): item is ActivitySource => item !== null);
      const duplicate = isDuplicateSource(selectedFile, existingSources);
      if (duplicate) {
        setIntakeConflict({ existing: duplicate, candidate: source, kind: "duplicate" });
        return;
      }
      if (activitySession.primary && activitySession.activityMetadata) {
        setIsParsingRide(true);
        setRideParseError(null);
        try {
          const rideSurfaceType = inferRideSurfaceType(selectedFile.name);
          const ftpSelection = selectFtpForRide(athleteProfile, rideSurfaceType, lastFtpSelection?.selectedPolicy ?? null);
          const result = await runRideUploadPipeline(selectedFile, fitParser, ftpSelection.ftpWatts, setUploadProgress, { runAnalysis: false });
          const match = matchActivityMetadata(activitySession.activityMetadata, getRideMetadata(result.ride), selectedFile);
          const parsedFit = classifyParsedFitSource(selectedFile, result.ride, result.diagnostics);
          const parsedSource = {
            ...source,
            role: "companion" as const,
            type: parsedFit.sourceType,
            confidence: parsedFit.confidence,
            fitDiagnostics: parsedFit.fitDiagnostics,
            status: "parsed" as const,
            note: match.confidence === "high"
              ? `Same ride · ${match.reasons.join(" · ")} · ${parsedFit.note}`
              : "This appears to be a different ride."
          };
          if (match.confidence === "high") {
            await activityFileStorage.put(parsedSource.id, selectedFile);
            setActivitySession(current => ({ ...current, supportingRideSources: [...current.supportingRideSources, parsedSource] }));
          } else {
            setPendingPrimaryCandidate({ source: parsedSource, ride: result.ride, diagnostics: result.diagnostics, ftpSelection });
            setIntakeConflict({ existing: activitySession.primary, candidate: parsedSource, kind: "primary-choice" });
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unable to inspect the selected FIT file.";
          setLastCaughtError(message);
          setRideParseError(message);
          setUploadProgress({ stage: "failed", label: "Upload failed", progress: 100 });
        } finally {
          setIsParsingRide(false);
        }
        return;
      }
      await activityFileStorage.put(source.id, selectedFile);
      setActivitySession(current => ({ ...current, primary: source }));
      setSelectedIntervalId(null);
      setTrainSubview("overview");
      await parseSelectedRide(selectedFile, source);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Primary upload failed.";
      setLastTrainAction("PRIMARY_PARSE_FAILED");
      setLastCaughtError(message);
      setRideParseError(message);
      setUploadProgress({ stage: "failed", label: "Upload failed", progress: 100 });
      setIsParsingRide(false);
      setActivitySession(current => ({
        ...current,
        primary: current.primary ? { ...current.primary, status: "failed", note: message } : current.primary
      }));
    }
  }

  async function addSources(files: FileList | null, role: "companion" | "screenshot") {
    if (!files) return;
    markTrainMutation(role === "companion" ? "COMPANION_FILE_SELECTED" : "SCREENSHOT_FILE_SELECTED");
    markAnalysisStaleIfComplete();
    const primaryStart = parsedRide?.rideDate
      ? new Date(parsedRide.rideDate).getTime()
      : activitySession.activityMetadata?.startTime ?? Number.NaN;
    const primaryDuration = parsedRide?.elapsedTime
      ?? parsedRide?.movingTime
      ?? activitySession.activityMetadata?.durationSeconds
      ?? null;
    const primaryRange = Number.isFinite(primaryStart) && primaryDuration
      ? { start: primaryStart, end: primaryStart + primaryDuration * 1000 }
      : undefined;
    const pendingSources = [activitySession.primary, ...activitySession.supportingRideSources, ...activitySession.companionSources].filter((source): source is ActivitySource => source !== null);
    for (const file of Array.from(files)) {
      let source = await classifyActivitySource(file, role, primaryRange);
      if (role === "screenshot" && source.type === "Unknown Screenshot" && pendingSources.some(candidate => candidate.type === "Tymewear CSV")) {
        source = { ...applyManualSourceLabel(source, "Tymewear"), confidence: "medium", note: "Potential Tymewear screenshot · Needs confirmation" };
      }
      const duplicate = isDuplicateSource(file, pendingSources);
      if (duplicate) {
        setIntakeConflict({ existing: duplicate, candidate: source, kind: "duplicate" });
        return;
      }
      if (/FIT/.test(source.type)) {
        await handlePrimaryFile(file);
        continue;
      }
      await activityFileStorage.put(source.id, file);
      setActivitySession(current => ({ ...current, companionSources: recognizeSessionScreenshots([...current.companionSources, source]) }));
      pendingSources.push(source);
    }
  }

  async function replaceSource(sourceId: string, files: FileList | null) {
    markTrainMutation("SOURCE_REPLACE_REQUESTED");
    const file = getSelectedUpload(files);
    if (!file) return;
    markAnalysisStaleIfComplete();
    if (activitySession.primary?.id === sourceId) {
      await handlePrimarySelection(files);
      return;
    }
    const existing = [...activitySession.supportingRideSources, ...activitySession.companionSources].find(source => source.id === sourceId);
    if (!existing) return;
    const primaryStart = parsedRide?.rideDate
      ? new Date(parsedRide.rideDate).getTime()
      : activitySession.activityMetadata?.startTime ?? Number.NaN;
    const primaryDuration = parsedRide?.elapsedTime
      ?? parsedRide?.movingTime
      ?? activitySession.activityMetadata?.durationSeconds
      ?? null;
    const primaryRange = Number.isFinite(primaryStart) && primaryDuration
      ? { start: primaryStart, end: primaryStart + primaryDuration * 1000 }
      : undefined;
    const replacement = await classifyActivitySource(file, existing.role, primaryRange);
    await activityFileStorage.remove(sourceId);
    await activityFileStorage.put(replacement.id, file);
    setActivitySession(current => ({
      ...current,
      supportingRideSources: current.supportingRideSources.map(source => source.id === sourceId ? replacement : source),
      companionSources: current.companionSources.map(source => source.id === sourceId ? replacement : source)
    }));
  }

  function clearParsedRide() {
    setLastFtpSelection(null);
    setAnalysisFtpSelection(null);
    setParsedRide(null);
    setRideParseError(null);
    setUploadProgress(null);
    setParserDiagnostics(null);
    setIsParsingRide(false);
    setSelectedIntervalId(null);
  }

  async function removeSource(sourceId: string) {
    markTrainMutation("SOURCE_REMOVED");
    markAnalysisStaleIfComplete();
    await activityFileStorage.remove(sourceId);
    if (activitySession.primary?.id === sourceId) {
      clearParsedRide();
      setActivitySession(current => ({ ...current, primary: null, activityMetadata: null }));
    } else {
      setActivitySession(current => ({
        ...current,
        supportingRideSources: current.supportingRideSources.filter(source => source.id !== sourceId),
        companionSources: current.companionSources.filter(source => source.id !== sourceId)
      }));
    }
  }

  function labelSource(sourceId: string, label: ManualSourceLabel) {
    markTrainMutation("SOURCE_LABELED");
    markAnalysisStaleIfComplete();
    const selected = activitySession.companionSources.find(source => source.id === sourceId);
    if (selected?.role === "companion" && selected.type === "Unknown CSV" && label === "Tymewear") {
      const primaryStart = parsedRide?.rideDate
        ? new Date(parsedRide.rideDate).getTime()
        : activitySession.activityMetadata?.startTime ?? Number.NaN;
      const primaryDuration = parsedRide?.elapsedTime
        ?? parsedRide?.movingTime
        ?? activitySession.activityMetadata?.durationSeconds
        ?? null;
      const primaryRange = Number.isFinite(primaryStart) && primaryDuration
        ? { start: primaryStart, end: primaryStart + primaryDuration * 1000 }
        : undefined;
      void (async () => {
        const labeled = await classifyActivitySource(selected.file, selected.role, primaryRange, { manualLabel: "Tymewear" });
        setActivitySession(current => ({
          ...current,
          companionSources: current.companionSources.map(source => source.id === sourceId
            ? {
                ...labeled,
                id: source.id,
                fingerprint: source.fingerprint,
                previewUrl: source.previewUrl,
                confirmedScreenshotValues: source.confirmedScreenshotValues
              }
            : source)
        }));
      })();
      return;
    }

    setActivitySession(current => ({ ...current, companionSources: current.companionSources.map(source => source.id === sourceId ? applyManualSourceLabel(source, label) : source) }));
  }

  async function clearActivity() {
    markTrainMutation("CLEAR_ACTIVITY");
    activitySession.companionSources.forEach(source => source.previewUrl && URL.revokeObjectURL(source.previewUrl));
    await activityFileStorage.clear();
    await clearPersistedTrainActivityState();
    setRestoredFromBrowser(false);
    setRestoreFailureMessage(null);
    setActivitySession(createActivitySession());
    setIntakeConflict(null);
    setAnalysisStage(null);
    setUnifiedAnalysis(null);
    setAnalysisFtpSelection(null);
    setAnalysisStatus("idle");
    setAnalysisCompletedAt(null);
    setRestoredAnalysisMessage(null);
    clearParsedRide();
    setPersistenceDiagnostics(current => ({
      ...current,
      currentSessionId: null,
      saveAttempted: false,
      saveResult: "idle",
      lastSavedTimestamp: null,
      approximateStoredSizeBytes: null,
      restoreResult: "idle",
      restoredParsedRidePresent: false,
      restoredCanonicalRidePresent: false,
      analysisResultPresentLive: false,
      analysisResultIncludedInSave: false,
      analysisSaveAttempted: false,
      analysisSaveResult: "idle",
      restoredAnalysisResultPresent: false,
      restoredAnalysisStatus: "idle",
      restoredTrainSubview: "overview",
      analysisStale: false,
      lastPersistenceError: null
    }));
  }

  async function clearSavedActivityAfterRestoreFailure() {
    await clearPersistedTrainActivityState();
    await clearActivity();
    setRestoreFailureMessage(null);
  }

  function cancelHistoricalImport() {
    historicalImportAbortRef.current?.abort();
  }

  async function handleHistoricalImportSelection(files: FileList | null) {
    const selectionReceivedAtEpochMs = epochNowMs();
    const selected = files ? Array.from(files) : [];
    if (selected.length === 0) {
      return;
    }

    const preImportHistorySnapshot = [...activityHistoryRecords];

    const controller = new AbortController();
    const startAtIso = new Date().toISOString();
    historicalImportAbortRef.current = controller;
    setHistoricalImportInProgress(true);
    setHistoricalImportProgressPercent(0);
    setHistoricalImportProgressStage("queued");
    setHistoricalImportProgressStatus("Preparing historical import");
    setHistoricalImportWorkerDiagnostics({
      stage: "queued",
      workerState: "starting",
      lastProgressAt: startAtIso
    });
    setHistoricalImportRuntimeDiagnostics({
      fileName: selected[0]?.name ?? null,
      fileSizeBytes: selected[0]?.size ?? null,
      workerStartedAt: startAtIso,
      currentStage: "queued",
      stageStartedAt: startAtIso,
      lastWorkerMessageAt: startAtIso,
      workerState: "starting",
      workerActive: true,
      cancellationAvailable: true,
      performanceDiagnostics: null
    });
    setHistoricalImportResult(null);

    await new Promise<void>(resolve => {
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => resolve());
        setTimeout(() => resolve(), 16);
      } else {
        setTimeout(() => resolve(), 0);
      }
    });

    try {
      const result = await importHistoricalRideBatch(selected, athleteProfile, {
        signal: controller.signal,
        selectionReceivedAtEpochMs,
        onProgress: progress => {
          const nowIso = new Date().toISOString();
          setHistoricalImportProgressPercent(progress.percent);
          setHistoricalImportProgressStage(progress.stage);
          setHistoricalImportProgressStatus(progress.status);
          setHistoricalImportWorkerDiagnostics(progress.workerDiagnostics);
          setHistoricalImportRuntimeDiagnostics(current => {
            const stageChanged = current?.currentStage !== progress.stage;
            const stageStart = stageChanged ? nowIso : (current?.stageStartedAt ?? nowIso);
            return {
              fileName: progress.row.fileName,
              fileSizeBytes: progress.row.fileSizeBytes,
              workerStartedAt: current?.workerStartedAt ?? nowIso,
              currentStage: progress.stage,
              stageStartedAt: stageStart,
              lastWorkerMessageAt: progress.workerDiagnostics.lastProgressAt ?? nowIso,
              workerState: progress.workerDiagnostics.workerState,
              workerActive: progress.workerDiagnostics.workerState === "running" || progress.workerDiagnostics.workerState === "starting",
              cancellationAvailable: !controller.signal.aborted,
              performanceDiagnostics: progress.workerDiagnostics.performanceDiagnostics ?? current?.performanceDiagnostics ?? null
            };
          });
          setHistoricalImportResult({
            rows: progress.rows,
            summary: progress.summary
          });
        }
      });
      setHistoricalImportResult(result);
      setHistoricalImportRuntimeDiagnostics(current => current ? {
        ...current,
        performanceDiagnostics: result.performanceDiagnostics ?? current.performanceDiagnostics ?? null
      } : current);
      if (result.summary.importsCompleted > 0) {
        await refreshCompletedActivityHistory();
        await refreshAthleteTimeline("historical-import");
      }
    } catch (error) {
      if (error instanceof HistoricalRideImportCancelledError) {
        setHistoricalImportProgressStatus("Import cancelled by user.");
        setHistoricalImportWorkerDiagnostics(current => current ? { ...current, workerState: "cancelled", lastProgressAt: new Date().toISOString() } : current);
        setHistoricalImportRuntimeDiagnostics(current => current ? {
          ...current,
          workerState: "cancelled",
          workerActive: false,
          cancellationAvailable: false,
          lastWorkerMessageAt: new Date().toISOString()
        } : current);
        setHistoricalImportResult(error.partialResult);
        setActivityHistoryRecords(preImportHistorySnapshot);
        return;
      }

      const message = error instanceof Error ? error.message : "Historical import failed.";
      setHistoricalImportResult(current => current ?? {
        rows: selected.map(file => ({
          fileName: file.name,
          fileSizeBytes: file.size,
          recognized: false,
          sourceType: "unknown",
          detectedRideDate: null,
          sport: null,
          durationSeconds: null,
          distanceMeters: null,
          parseStatus: "failed",
          duplicateStatus: "not_checked",
          persistenceStatus: "failed",
          failureType: "parse_failed",
          message,
          duplicateIdentity: null
        })),
        summary: {
          filesScanned: selected.length,
          filesRecognized: 0,
          filesRejected: 0,
          duplicatesDetected: 0,
          importsCompleted: 0,
          importsFailed: selected.length,
          ridesCreated: 0,
          canonicalMatches: 0,
          provenanceMerges: 0,
          failedParses: selected.length,
          cancelled: 0,
          persistenceFailures: 0
        }
      });
      setActivityHistoryRecords(preImportHistorySnapshot);
    } finally {
      historicalImportAbortRef.current = null;
      setHistoricalImportInProgress(false);
      setHistoricalImportProgressStatus(current => current ?? "Idle");
      setHistoricalImportRuntimeDiagnostics(current => current ? {
        ...current,
        workerActive: false,
        cancellationAvailable: false
      } : current);
    }
  }

  function startNewActivityAfterRestoreFailure() {
    setRestoreFailureMessage(null);
    setActivitySession(createActivitySession());
    clearParsedRide();
  }

  async function resolveConflict(choice: "create" | "replace" | "cancel") {
    markTrainMutation("RESOLVE_INTAKE_CONFLICT");
    const conflict = intakeConflict;
    setIntakeConflict(null);
    if (!conflict || choice === "cancel" || conflict.kind === "duplicate") {
      setPendingPrimaryCandidate(null);
      return;
    }
    if (pendingPrimaryCandidate) {
      const primary = { ...pendingPrimaryCandidate.source, role: "primary" as const, status: "parsed" as const };
      if (choice === "create") {
        await activityFileStorage.clear();
      } else {
        await activityFileStorage.remove(conflict.existing.id);
      }
      await activityFileStorage.put(primary.id, primary.file);
      setActivitySession(current => choice === "create"
        ? { ...createActivitySession(), primary, activityMetadata: getRideMetadata(pendingPrimaryCandidate.ride) }
        : { ...current, primary, activityMetadata: getRideMetadata(pendingPrimaryCandidate.ride) });
      setParsedRide(pendingPrimaryCandidate.ride);
      setParserDiagnostics(pendingPrimaryCandidate.diagnostics);
      setLastFtpSelection(pendingPrimaryCandidate.ftpSelection);
      setAnalysisFtpSelection(null);
      setPendingPrimaryCandidate(null);
      return;
    }
  }

  async function runAnalysis() {
    markTrainMutation("RUN_ANALYSIS");
    if (!parsedRide || activitySession.primary?.status !== "parsed") return;
    const hadCompletedSnapshot = analysisStatus === "complete" && unifiedAnalysis !== null;
    for (const stage of ["Validating sources", "Parsing primary ride", "Preparing companion data", "Running ride analysis"]) {
      setAnalysisStage(stage);
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
    }
    const ftpSelection = getCurrentFtpSelection();
    const analyzedRide = applyRideAnalytics({ ...parsedRide }, ftpSelection.ftpWatts);
    const unifiedInput = await buildUnifiedActivityAnalysisInput(activitySession, analyzedRide, ftpSelection.ftpWatts, {
      parseSupportingRide: async source => {
        const result = await runRideUploadPipeline(source.file, fitParser, ftpSelection.ftpWatts, undefined, { runAnalysis: false });
        return result.ride;
      }
    });
    const analysisResult = runUnifiedActivityAnalysis(unifiedInput);
    const completedAt = new Date().toISOString();

    const sessionForSave = ensureSessionId(activitySession);
    if (sessionForSave.id !== activitySession.id) {
      setActivitySession(sessionForSave);
    }

    const applyCompletedAnalysisState = () => {
      setParsedRide(analyzedRide);
      setUnifiedAnalysis(analysisResult);
      setAnalysisFtpSelection(ftpSelection);
      setAnalysisStatus("complete");
      setAnalysisCompletedAt(completedAt);
      setTrainSubview(current => reduceTrainSubview(current, "open-analysis"));
      setLastTrainAction("ANALYSIS_SUCCEEDED");
    };

    try {
      await persistCompletedSession({
        activitySession: sessionForSave,
        parsedRide: analyzedRide,
        unifiedAnalysis: analysisResult,
        lastFtpSelection,
        analysisFtpSelection: ftpSelection,
        parserDiagnostics,
        trainSubview: "analysis",
        selectedIntervalId,
        analysisStatus: "complete",
        analysisCompletedAt: completedAt,
        reason: "ANALYSIS_SUCCEEDED"
      });
      applyCompletedAnalysisState();
      setAnalysisStage("Analysis complete");
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      setAnalysisStage(null);
      setPersistenceDiagnostics(current => ({
        ...current,
        analysisSaveAttempted: true,
        analysisSaveResult: "success",
        analysisResultIncludedInSave: true,
        analysisResultPresentLive: true,
        analysisStale: false
      }));
    } catch (error) {
      tracePersistenceError("Promise rejected: analysis completion persistence", error);
      const message = error instanceof Error ? error.message : "Unable to persist completed analysis.";
      if (!hadCompletedSnapshot) {
        applyCompletedAnalysisState();
      } else {
        setLastTrainAction("ANALYSIS_FAILED_PERSISTENCE_RETAINED_PREVIOUS");
      }
      setAnalysisStage(null);
      setPersistenceDiagnostics(current => ({
        ...current,
        analysisSaveAttempted: true,
        analysisSaveResult: "failed",
        lastPersistenceError: message
      }));
    }
  }

  useEffect(() => {
    setSelectedIntervalId(null);
  }, [parsedRide?.sourceFileName, parsedRide?.sourceFileSizeBytes, parsedRide?.records.length]);

  useEffect(() => {
    let cancelled = false;

    setPersistenceDiagnostics(current => ({
      ...current,
      databaseOpenStatus: getActivitySessionStoreStatus()
    }));

    void (async () => {
      tracePersistence("open database");
      setPersistenceDiagnostics(current => ({
        ...current,
        databaseOpenStatus: "opening"
      }));

      tracePersistence("awaiting Promise: initializeActivitySessionStore");
      const initialized = await initializeActivitySessionStore();
      tracePersistence("Promise resolved: initializeActivitySessionStore", initialized.databaseStatus);
      if (cancelled) {
        tracePersistence("database initialization returned early", { reason: "effect was cancelled" });
        return;
      }

      tracePersistence("database opened", {
        backend: initialized.backend,
        status: initialized.databaseStatus
      });

      setPersistenceDiagnostics(current => ({
        ...current,
        storageBackend: initialized.backend,
        databaseOpenStatus: initialized.databaseStatus
      }));
    })().catch(error => {
      tracePersistenceError("Promise rejected: database initialization effect", error);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const [records, sessions] = await Promise.all([
          listDailyHealthRecords(),
          listHealthImportSessions()
        ]);
        if (cancelled) {
          return;
        }
        setDailyHealthRecords(records);
        setHealthImportSessions(sessions);
        if (records.length > 0) {
          setSelectedDailyHealthDate(current => current ?? records[0].localDate);
        }
      } catch {
        if (!cancelled) {
          setDailyHealthRecords([]);
          setHealthImportSessions([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        await refreshAthleteTimeline("loaded");
        if (cancelled) return;
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : "Unable to load timeline.";
          setTimelineDiagnostics(current => ({
            ...current,
            restoreResult: `failed:${message}`,
            lastPersistenceError: message
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      setHistoryPersistenceStatus("loading");
      try {
        const records = await listCompletedActivityHistoryRecords();
        if (!cancelled) {
          setActivityHistoryRecords(records);
          setHistoryPersistenceStatus("loaded");
          setPersistenceDiagnostics(current => ({
            ...current,
            historyPersistenceStatus: "loaded",
            historyRecordCount: records.length
          }));
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : "Unable to load completed activity history.";
          setHistoryPersistenceStatus(`failed:${message}`);
          setPersistenceDiagnostics(current => ({
            ...current,
            historyPersistenceStatus: `failed:${message}`
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function restorePersistedState() {
      tracePersistence("hydrate begin");
      setHydrationLifecycle(current => transitionHydrationLifecycle(current, "start"));
      setPersistenceDiagnostics(current => ({
        ...current,
        hydrationLifecycle: "hydrating",
        restoreAttempted: true,
        restoreResult: "idle",
        lastPersistenceError: null
      }));
      setLastTrainAction("HYDRATION_STARTED");
      let terminalHydrationEvent: "complete" | "fail" = "complete";
      let terminalHydrationLifecycle: HydrationLifecycle = "hydrated";
      let terminalRestoreResult: PersistenceDiagnostics["restoreResult"] = "not_found";
      let terminalRestoreError: string | null = null;
      try {
        tracePersistence("restore begin");
        tracePersistence("awaiting Promise: loadPersistedTrainActivityState");
        const persistedLoad = await loadPersistedTrainActivityState();
        tracePersistence("Promise resolved: loadPersistedTrainActivityState", {
          sessionId: persistedLoad.sessionId,
          statePresent: persistedLoad.state !== null
        });
        if (cancelled) {
          tracePersistence("restore returned early", { reason: "effect was cancelled after loading persisted state" });
          return;
        }

        setPersistenceDiagnostics(current => ({
          ...current,
          storageBackend: persistedLoad.backend,
          databaseOpenStatus: persistedLoad.databaseStatus,
          currentSessionId: persistedLoad.sessionId,
          restoreResult: "idle",
          lastPersistenceError: null,
          hydrationLifecycle: "hydrating"
        }));

        if (!persistedLoad.state) {
          tracePersistence("restore complete", {
            result: "not_found",
            reason: persistedLoad.sessionId ? "session pointer has no persisted payload" : "current session pointer is missing"
          });
          setPersistenceReady(true);
          terminalRestoreResult = "not_found";
          setPersistenceDiagnostics(current => ({
            ...current,
            restoreResult: "not_found",
            restoredParsedRidePresent: false,
            restoredCanonicalRidePresent: false,
            restoredAnalysisResultPresent: false,
            restoredAnalysisStatus: "idle",
            restoredTrainSubview: "overview"
          }));
          setLastTrainAction("HYDRATION_COMPLETED");
          return;
        }

        terminalRestoreResult = "success";
        const persisted = persistedLoad.state;
        tracePersistence("awaiting Promise: restoreActivitySessionWithRecovery", { sessionId: persisted.sessionId });
        const restored = await restoreActivitySessionWithRecovery(persisted.activitySession, activityFileStorage);
        tracePersistence("Promise resolved: restoreActivitySessionWithRecovery", { sessionId: persisted.sessionId });
        if (cancelled) {
          tracePersistence("restore returned early", { reason: "effect was cancelled after restoring activity files" });
          return;
        }

        if (!shouldApplyHydratedSnapshot("hydrating", liveTrainMutationRef.current)) {
          tracePersistence("restore returned early", { reason: "live train state changed during hydration" });
          setPersistenceReady(true);
          setLastTrainAction("HYDRATION_SKIPPED_LIVE_STATE");
          setPersistenceDiagnostics(current => ({
            ...current,
            restoreResult: "success"
          }));
          return;
        }

        const restoredSession = restored.session;
        const restoredUnifiedAnalysis = hasRestorableUnifiedAnalysis(persisted.unifiedAnalysis) ? persisted.unifiedAnalysis : null;
        const restoredAnalysisStatus: PersistedAnalysisStatus = persisted.analysisStatus ?? (restoredUnifiedAnalysis ? "complete" : "idle");
        const canOpenRestoredAnalysis = restoredAnalysisStatus === "complete"
          && restoredUnifiedAnalysis !== null
          && persisted.parsedRide !== null
          && persisted.parsedRide.records.length > 0;
        const restoredSubview: TrainSubview = persisted.trainSubview === "analysis"
          ? (canOpenRestoredAnalysis ? "analysis" : "overview")
          : persisted.trainSubview === "timeline"
            ? "timeline"
            : "overview";

        setActivitySession(restoredSession);
        setParsedRide(persisted.parsedRide);
        setUnifiedAnalysis(restoredUnifiedAnalysis);
        setAnalysisStatus(canOpenRestoredAnalysis ? restoredAnalysisStatus : "idle");
        setAnalysisCompletedAt(canOpenRestoredAnalysis ? (persisted.analysisCompletedAt ?? null) : null);
        setLastFtpSelection(persisted.lastFtpSelection);
        setAnalysisFtpSelection(persisted.analysisFtpSelection);
        setParserDiagnostics(persisted.parserDiagnostics);
        setTrainSubview(restoredSubview);
        setRestoredAnalysisMessage(restoredSubview === "analysis" && canOpenRestoredAnalysis ? "Completed analysis restored from this browser" : null);
        setSelectedIntervalId(persisted.selectedIntervalId);
        setRestoredFromBrowser(Boolean(restoredSession.primary));
        if (restoredSession.primary?.status === "needs_reupload") {
          setRideParseError(restoredSession.primary.note ?? "This activity upload was interrupted. Re-select the primary FIT file to continue.");
          setParsedRide(null);
          setParserDiagnostics(null);
        }

        setPersistenceReady(true);
        setPersistenceDiagnostics(current => ({
          ...current,
          restoreResult: "success",
          schemaVersion: persisted.schemaVersion,
          restoredParsedRidePresent: persisted.parsedRide !== null && persisted.parsedRide.records.length > 0,
          restoredCanonicalRidePresent: restoredSession.activityMetadata !== null,
          analysisResultPresentLive: restoredUnifiedAnalysis !== null,
          restoredAnalysisResultPresent: restoredUnifiedAnalysis !== null,
          restoredAnalysisStatus: canOpenRestoredAnalysis ? restoredAnalysisStatus : "idle",
          analysisResultIncludedInSave: restoredUnifiedAnalysis !== null,
          restoredTrainSubview: restoredSubview,
          analysisStale: restoredAnalysisStatus === "stale"
        }));
        setLastTrainAction("HYDRATION_COMPLETED");
        tracePersistence("restore complete", { result: "success", sessionId: persisted.sessionId });

        if (restored.resumePrimary) {
          tracePersistence("restore resuming interrupted primary parse", { sourceId: restored.resumePrimary.source.id });
          void parseSelectedRide(restored.resumePrimary.file, restored.resumePrimary.source);
        }
      } catch (error) {
        tracePersistenceError("Promise rejected: restore persistence pipeline", error);
        const message = error instanceof Error ? error.message : "Unable to load persisted activity state.";
        terminalHydrationEvent = "fail";
        terminalHydrationLifecycle = "failed";
        terminalRestoreResult = "failed";
        terminalRestoreError = message;
        if (!cancelled) {
          setRestoreFailureMessage("A saved activity could not be restored.");
          setLastCaughtError(message);
          setPersistenceReady(true);
          setPersistenceDiagnostics(current => ({
            ...current,
            databaseOpenStatus: "failed",
            restoreResult: "failed",
            restoredAnalysisResultPresent: false,
            restoredAnalysisStatus: "idle",
            restoredTrainSubview: "overview",
            lastPersistenceError: message
          }));
          setLastTrainAction("HYDRATION_COMPLETED");
        }
      } finally {
        if (!cancelled) {
          setHydrationLifecycle(current => transitionHydrationLifecycle(current, terminalHydrationEvent));
          setPersistenceDiagnostics(current => ({
            ...current,
            hydrationLifecycle: terminalHydrationLifecycle,
            restoreResult: terminalRestoreResult,
            lastPersistenceError: terminalRestoreError ?? current.lastPersistenceError
          }));
          tracePersistence("hydration complete", {
            lifecycle: terminalHydrationLifecycle,
            restoreResult: terminalRestoreResult
          });
        } else {
          tracePersistence("hydration completion skipped", { reason: "effect was cancelled" });
        }
      }
    }

    void restorePersistedState();

    return () => {
      cancelled = true;
    };
  }, [activityFileStorage]);

  useEffect(() => {
    if (!persistenceReady || !isTerminalHydrationLifecycle(hydrationLifecycle)) {
      tracePersistence("queued save check returned early", {
        reason: "persistence is not ready or hydration is not terminal",
        persistenceReady,
        hydrationLifecycle
      });
      return;
    }

    if (!pendingPersistenceSaveRef.current) {
      tracePersistence("queued save check returned early", { reason: "no queued persistence save" });
      return;
    }

    const queuedSave = pendingPersistenceSaveRef.current;
    pendingPersistenceSaveRef.current = null;
    void persistCompletedSession({
      ...queuedSave,
      reason: `${queuedSave.reason}:queued-after-hydration`
    });
  }, [persistenceReady, hydrationLifecycle]);

  useEffect(() => {
    if (!persistenceReady || !isTerminalHydrationLifecycle(hydrationLifecycle)) {
      tracePersistence("state save effect returned early", {
        reason: "persistence is not ready or hydration is not terminal",
        persistenceReady,
        hydrationLifecycle
      });
      return;
    }

    if (!activitySession.id || activitySession.id.trim().length === 0) {
      tracePersistence("state save effect returned early", { reason: "session ID is missing; scheduling ID assignment" });
      setActivitySession(current => ensureSessionId(current));
      return;
    }

    if (hasTransientSources(activitySession) || isParsingRide || uploadProgress?.stage === "reading" || uploadProgress?.stage === "validating" || uploadProgress?.stage === "decompressing" || uploadProgress?.stage === "parsing" || uploadProgress?.stage === "building" || uploadProgress?.stage === "analyzing") {
      tracePersistence("state save effect returned early", {
        reason: "train state is transient",
        sessionId: activitySession.id,
        primaryStatus: activitySession.primary?.status ?? null,
        isParsingRide,
        uploadProgressStage: uploadProgress?.stage ?? null
      });
      setLastPersistenceWriteState("skipped: transient-state");
      setPersistenceDiagnostics(current => ({
        ...current,
        saveAttempted: true,
        saveResult: "skipped",
        hydrationLifecycle
      }));
      return;
    }

    if (!isStableCompletedTrainState({ activitySession, parsedRide, isParsingRide })) {
      tracePersistence("state save effect returned early", {
        reason: "train state is not stable and complete",
        sessionId: activitySession.id,
        primaryStatus: activitySession.primary?.status ?? null,
        parsedRidePresent: parsedRide !== null && parsedRide.records.length > 0,
        canonicalRidePresent: activitySession.activityMetadata !== null
      });
      setLastPersistenceWriteState("skipped: incomplete-state");
      setPersistenceDiagnostics(current => ({
        ...current,
        saveAttempted: true,
        saveResult: "skipped",
        hydrationLifecycle
      }));
      return;
    }

    void (async () => {
      try {
        tracePersistence("save begin", { reason: "stable state effect", sessionId: activitySession.id });
        tracePersistence("awaiting Promise: savePersistedTrainActivityState", { sessionId: activitySession.id });
        const saveResult = await savePersistedTrainActivityState(createPersistedTrainActivityState({
          activitySession,
          parsedRide,
          unifiedAnalysis,
          analysisStatus,
          analysisCompletedAt,
          lastFtpSelection,
          analysisFtpSelection,
          parserDiagnostics,
          trainSubview,
          selectedIntervalId,
          canAnalyze: true
        }));
        tracePersistence("Promise resolved: savePersistedTrainActivityState", { sessionId: saveResult.sessionId });
        tracePersistence("save complete", { sessionId: saveResult.sessionId, savedAt: saveResult.savedAt });
        setLastPersistenceWriteState("saved");
        setPersistenceDiagnostics(current => ({
          ...current,
          storageBackend: saveResult.backend,
          databaseOpenStatus: saveResult.databaseStatus,
          currentSessionId: saveResult.sessionId,
          saveAttempted: true,
          saveResult: "success",
          lastSavedTimestamp: saveResult.savedAt,
          schemaVersion: saveResult.schemaVersion,
          approximateStoredSizeBytes: saveResult.approximateSizeBytes,
          analysisResultPresentLive: unifiedAnalysis !== null,
          analysisResultIncludedInSave: unifiedAnalysis !== null,
          analysisStale: analysisStatus === "stale",
          hydrationLifecycle,
          lastPersistenceError: null
        }));
      } catch (error) {
        tracePersistenceError("Promise rejected: stable state persistence effect", error);
        const message = error instanceof Error ? error.message : "Unable to persist activity state.";
        setLastCaughtError(message);
        setLastPersistenceWriteState(`failed: ${message}`);
        setPersistenceDiagnostics(current => ({
          ...current,
          saveAttempted: true,
          saveResult: "failed",
          hydrationLifecycle,
          lastPersistenceError: message
        }));
      }
    })();
  }, [activitySession, parsedRide, unifiedAnalysis, analysisStatus, analysisCompletedAt, lastFtpSelection, analysisFtpSelection, parserDiagnostics, trainSubview, selectedIntervalId, persistenceReady, hydrationLifecycle, isParsingRide, uploadProgress]);

  useEffect(() => {
    if (!unifiedAnalysis || !parsedRide || !activitySession.primary) {
      setBaselineResult(null);
      return;
    }

    const currentRecord = buildCurrentCompletedActivityRecordSnapshot({
      ride: parsedRide,
      analysis: unifiedAnalysis,
      ftpSelection: analysisFtpSelection,
      completedAt: analysisCompletedAt
    });
    const personalBaseline = buildPersonalBaselineResult(currentRecord, activityHistoryRecords);
    setBaselineResult(personalBaseline);
  }, [unifiedAnalysis, parsedRide, activitySession.primary, analysisFtpSelection, analysisCompletedAt, activityHistoryRecords]);

  useEffect(() => {
    if (analysisStatus !== "complete" || !unifiedAnalysis || !parsedRide || !activitySession.primary) {
      return;
    }

    void persistCompletedActivityHistory({
      ride: parsedRide,
      analysis: unifiedAnalysis,
      ftpSelection: analysisFtpSelection,
      completedAt: analysisCompletedAt
    }).catch(error => {
      const message = error instanceof Error ? error.message : "Unable to persist completed activity history.";
      setHistoryPersistenceStatus(`failed:${message}`);
      setPersistenceDiagnostics(current => ({
        ...current,
        historyPersistenceStatus: `failed:${message}`
      }));
    });
  }, [analysisStatus, unifiedAnalysis, parsedRide, activitySession.primary, analysisFtpSelection, analysisCompletedAt]);

  useEffect(() => {
    if (analysisStatus !== "complete" || !unifiedAnalysis || !parsedRide || !activitySession.primary) {
      return;
    }

    const snapshot = buildCurrentCompletedActivityRecordSnapshot({
      ride: parsedRide,
      analysis: unifiedAnalysis,
      ftpSelection: analysisFtpSelection,
      completedAt: analysisCompletedAt
    });

    const key = `${buildAutomaticActivityStableSourceKey(snapshot)}:${snapshot.ftpSnapshot.ftpWatts ?? "none"}:${snapshot.analysisConfidence.overallPercent}:${snapshot.rideClassification.type}`;
    if (lastTimelineActivityUpdateKeyRef.current === key) {
      return;
    }
    lastTimelineActivityUpdateKeyRef.current = key;

    void (async () => {
      const timelineResults = await Promise.all([
        upsertAthleteTimelineEvent(createRideContextEvent(snapshot)),
        upsertAthleteTimelineEvent(createActivityCompletedTimelineEvent(snapshot)),
        upsertAthleteTimelineEvent(createRideAnalysisContextEvent(snapshot)),
        upsertAthleteTimelineEvent(createFtpSnapshotContextEvent(snapshot))
      ]);
      const primaryResult = timelineResults[timelineResults.length - 1];
      let raceResultAction: "created" | "updated" | "unchanged" | null = null;
      if (snapshot.rideClassification.type === "race") {
        const raceResult = await upsertAthleteTimelineEvent(createRaceTimelineEvent(snapshot));
        raceResultAction = raceResult.action;
      }
      const createdCount = timelineResults.filter(result => result.action === "created").length + (raceResultAction === "created" ? 1 : 0);
      const unchangedCount = timelineResults.filter(result => result.action === "unchanged").length + (raceResultAction === "unchanged" ? 1 : 0);

      setTimelineDiagnostics(current => ({
        ...current,
        automaticEventsCreated: current.automaticEventsCreated + createdCount,
        duplicateEventsPrevented: current.duplicateEventsPrevented + unchangedCount,
        saveResult: `${primaryResult.action}:${primaryResult.backend}:${primaryResult.databaseStatus}`,
        generatedEventId: primaryResult.event.id,
        generatedStableSourceKey: primaryResult.stableSourceKey
      }));
      await refreshAthleteTimeline("auto-analysis");
    })().catch(error => {
      const message = error instanceof Error ? error.message : "Unable to upsert activity timeline event.";
      setTimelineDiagnostics(current => ({
        ...current,
        saveResult: `failed:${message}`
      }));
    });
  }, [analysisStatus, unifiedAnalysis, parsedRide, activitySession.primary, analysisFtpSelection, analysisCompletedAt]);

  useEffect(() => {
    const currentFtp = athleteProfile.performance.ftpWatts;
    const previousFtp = previousFtpWattsRef.current;
    if (previousFtp === null) {
      previousFtpWattsRef.current = currentFtp;
      return;
    }

    if (previousFtp === currentFtp) {
      return;
    }

    previousFtpWattsRef.current = currentFtp;

    void (async () => {
      const result = await upsertAthleteTimelineEvent(createFtpChangeTimelineEvent({
        profile: athleteProfile,
        previousFtpWatts: previousFtp,
        nextFtpWatts: currentFtp
      }));
      setTimelineDiagnostics(current => ({
        ...current,
        automaticEventsCreated: current.automaticEventsCreated + (result.action === "created" ? 1 : 0),
        duplicateEventsPrevented: current.duplicateEventsPrevented + (result.action === "unchanged" ? 1 : 0),
        saveResult: `${result.action}:${result.backend}:${result.databaseStatus}`,
        generatedEventId: result.event.id,
        generatedStableSourceKey: result.stableSourceKey
      }));
      await refreshAthleteTimeline("auto-ftp-change");
    })().catch(error => {
      const message = error instanceof Error ? error.message : "Unable to upsert FTP change timeline event.";
      setTimelineDiagnostics(current => ({
        ...current,
        saveResult: `failed:${message}`
      }));
    });
  }, [athleteProfile]);

  const canRunCompleteAnalysis = activitySession.primary?.status === "parsed" && parsedRide !== null && parsedRide.records.length > 0 && !isParsingRide && !rideParseError;
  const readinessTitle = !activitySession.primary
    ? "Waiting for primary activity"
    : activitySession.primary.status === "needs_reupload" || activitySession.primary.status === "failed" || activitySession.primary.status === "error"
      ? "Primary activity needs attention"
      : activitySession.primary.status === "validating" || activitySession.primary.status === "parsing" || isParsingRide
        ? "Parsing primary activity"
        : canRunCompleteAnalysis
          ? "Ready to analyze"
          : "Waiting for primary activity";
  const readinessBlockingReason = canRunCompleteAnalysis
    ? null
    : !activitySession.primary
      ? "missing-primary"
      : activitySession.primary.status === "needs_reupload"
        ? "interrupted-upload-needs-reupload"
        : activitySession.primary.status === "failed" || activitySession.primary.status === "error"
          ? "primary-parse-failed"
          : activitySession.primary.status === "validating" || activitySession.primary.status === "parsing" || isParsingRide
            ? "primary-parse-in-progress"
            : parsedRide === null || parsedRide.records.length === 0
              ? "parsed-ride-missing"
              : "analysis-stage-active";
    const indexedRideHistory = historyDateRange.rides.start && historyDateRange.rides.end
      ? athleteHistoryIndex.getRidesBetween(historyDateRange.rides.start, historyDateRange.rides.end)
      : [];
    const indexedDailyHealth = historyDateRange.dailyHealth.start && historyDateRange.dailyHealth.end
      ? athleteHistoryIndex.getDailyHealthBetween(historyDateRange.dailyHealth.start, historyDateRange.dailyHealth.end)
      : [];
    const currentFtpSelection = getCurrentFtpSelection();
    const analysisSnapshot = analysisFtpSelection ?? currentFtpSelection;

    useEffect(() => {
      if (analysisStatus !== "complete" || !analysisFtpSelection) {
        return;
      }

      if (analysisFtpSelection.ftpWatts !== currentFtpSelection.ftpWatts) {
        setAnalysisStatus("stale");
        setRestoredAnalysisMessage(null);
        setPersistenceDiagnostics(current => ({
          ...current,
          analysisStale: true
        }));
      }
    }, [analysisStatus, analysisFtpSelection, currentFtpSelection.ftpWatts]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <button type="button" className="icon-button mobile-only" onClick={() => setMenuOpen(true)} aria-label="Open menu" aria-expanded={menuOpen}>
          <Menu size={24} />
        </button>
        <div className="brand">
          <div className="brand-mark"><Bike size={24} /></div>
          <div>
            <strong>Johan Performance Lab</strong>
            <span>Integrated athlete intelligence</span>
          </div>
        </div>
        <div className="status-pill"><ShieldCheck size={16} /> Local-first</div>
      </header>

      <aside className={`sidebar ${menuOpen ? "open" : ""}`}>
        <div className="sidebar-title">
          <span>Navigation</span>
          <button type="button" className="icon-button mobile-only" onClick={() => setMenuOpen(false)} aria-label="Close menu">
            <X size={22}/>
          </button>
        </div>
        <nav>
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <button type="button" key={item.id} className={active === item.id ? "active" : ""} onClick={() => selectPage(item.id)} aria-current={active === item.id ? "page" : undefined}>
                <Icon size={20} /><span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <Database size={18} />
          <div><strong>Sprint 1</strong><span>Athlete command center</span></div>
        </div>
      </aside>

      {menuOpen && <button type="button" className="scrim mobile-only" onClick={() => setMenuOpen(false)} aria-label="Close menu" />}

      <main>
        {active === "home" && (
          <Page title="Athlete Command Center" eyebrow="HOME">
            <AthleteCommandCenterPage
              onGoToTrain={() => selectPage("train")}
              onOpenRideAnalysis={activityId => void openHistoryAnalysis(activityId)}
              athleteIntelligence={athleteIntelligence}
              activityHistoryRecords={indexedRideHistory}
              dailyHealthRecords={indexedDailyHealth}
              contextEvents={athleteHistoryIndex.getTimelineEvents()}
            />
          </Page>
        )}

        {active === "train" && (
          <Page title="Train" eyebrow="DAILY EXECUTION">
            <div className="train-subview-nav" role="tablist" aria-label="Train views">
              <button
                type="button"
                role="tab"
                aria-selected={trainSubview === "overview"}
                className={`train-subview-button ${trainSubview === "overview" ? "active" : ""}`}
                onClick={() => setTrainSubview(current => reduceTrainSubview(current, "open-overview"))}
              >
                Overview
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={trainSubview === "timeline"}
                className={`train-subview-button ${trainSubview === "timeline" ? "active" : ""}`}
                onClick={() => setTrainSubview(current => reduceTrainSubview(current, "open-timeline"))}
              >
                Athlete Timeline
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={trainSubview === "analysis"}
                className={`train-subview-button ${trainSubview === "analysis" ? "active" : ""}`}
                onClick={() => setTrainSubview(current => reduceTrainSubview(current, "open-analysis"))}
              >
                Analysis
              </button>
            </div>
            <TrainWorkflowErrorBoundary
              snapshot={{
                activeRoute: active,
                trainSubview,
                primarySourceStatus: activitySession.primary?.status ?? null,
                isParsingRide,
                uploadProgressStage: uploadProgress?.stage ?? null,
                hydrationLifecycle
              }}
              onReturnToTrain={() => {
                setActive("train");
                setTrainSubview("overview");
              }}
              onClearInterruptedActivity={() => {
                void clearActivity();
              }}
            >
              {trainSubview === "overview" && (
                <TrainLandingPage
                  activitySession={activitySession}
                  ride={parsedRide}
                  rideParseError={rideParseError}
                  uploadProgress={uploadProgress}
                  analysisStage={analysisStage}
                  parserDiagnostics={parserDiagnostics}
                  conflict={intakeConflict}
                  restoredFromBrowser={restoredFromBrowser}
                  restoreFailureMessage={restoreFailureMessage}
                  analysisAvailable={unifiedAnalysis !== null}
                  analysisStale={analysisStatus === "stale"}
                  historyRecords={activityHistoryRecords}
                  ftpSelection={currentFtpSelection}
                  historicalImportResult={historicalImportResult}
                  historicalImportInProgress={historicalImportInProgress}
                  historicalImportProgressPercent={historicalImportProgressPercent}
                  historicalImportProgressStage={historicalImportProgressStage}
                  historicalImportProgressStatus={historicalImportProgressStatus}
                  historicalImportWorkerDiagnostics={historicalImportWorkerDiagnostics}
                  historicalImportRuntimeDiagnostics={historicalImportRuntimeDiagnostics}
                  onHistoricalImportSelectFiles={files => void handleHistoricalImportSelection(files)}
                  onCancelHistoricalImport={cancelHistoricalImport}
                  historySummary={{
                    ridesTotal: athleteHistoryIndex.getRideCount(),
                    ridesFirstDate: historyDateRange.rides.start,
                    ridesLatestDate: historyDateRange.rides.end,
                    dailyHealthTotal: athleteHistoryIndex.getHealthRecordCount(),
                    dailyHealthFirstDate: historyDateRange.dailyHealth.start,
                    dailyHealthLatestDate: historyDateRange.dailyHealth.end,
                    sources: historySourceSummary
                  }}
                  onOpenAnalysis={() => setTrainSubview(current => reduceTrainSubview(current, "open-analysis"))}
                  onOpenHistoryAnalysis={activityId => void openHistoryAnalysis(activityId)}
                  onDeleteHistoryActivity={activityId => void deleteHistoryActivity(activityId)}
                  onClearSavedActivity={() => void clearSavedActivityAfterRestoreFailure()}
                  onStartNewActivity={startNewActivityAfterRestoreFailure}
                  onFtpSelectionChange={handleFtpSelectionChange}
                  debugState={{
                    hydrationLifecycle,
                    primarySourceId: activitySession.primary?.id ?? null,
                    primarySourceStatus: activitySession.primary?.status ?? null,
                    isParsingRide,
                    uploadProgressStage: uploadProgress?.stage ?? null,
                    parsedRidePresent: parsedRide !== null && parsedRide.records.length > 0,
                    canonicalMetadataPresent: Boolean(activitySession.activityMetadata),
                    readinessResult: readinessTitle,
                    readinessBlockingReason,
                    lastReducerAction: lastTrainAction,
                    lastPersistenceWriteState,
                    lastCaughtError,
                    persistenceDiagnostics
                  }}
                  onPrimarySelection={files => void handlePrimarySelection(files)}
                  onCompanionSelection={files => void addSources(files, "companion")}
                  onScreenshotSelection={files => void addSources(files, "screenshot")}
                  onReplaceSource={(sourceId, files) => void replaceSource(sourceId, files)}
                  onRemoveSource={sourceId => void removeSource(sourceId)}
                  onSourceLabel={labelSource}
                  onClearActivity={() => void clearActivity()}
                  onRunAnalysis={() => void runAnalysis()}
                  onResolveConflict={choice => void resolveConflict(choice)}
                />
              )}
              {trainSubview === "timeline" && (
                <div className="train-layout">
                  <Card>
                    <AthleteTimelinePanel
                      events={athleteTimelineEvents}
                      preferredUnits={athleteProfile.personal.preferredUnits}
                      diagnostics={timelineDiagnostics}
                      onOpenRelatedActivity={activityId => void openHistoryAnalysis(activityId)}
                      onOpenDailyHealthDate={localDate => {
                        setRecoverySubview("daily-health");
                        setSelectedDailyHealthDate(localDate);
                        selectPage("recovery");
                      }}
                      onSaveManualEvent={saveTimelineManualEvent}
                      onDeleteEvent={id => void deleteTimelineEvent(id)}
                    />
                  </Card>
                </div>
              )}
              {trainSubview === "analysis" && (
                <RideAnalysisWorkspace
                  ride={parsedRide ?? emptyRide}
                  parsedRide={parsedRide}
                  ftpSelection={currentFtpSelection}
                  analysisFtpSelection={analysisSnapshot}
                  selectedIntervalId={selectedIntervalId}
                  onSelectedIntervalChange={setSelectedIntervalId}
                  isParsingRide={isParsingRide}
                  rideParseError={rideParseError}
                  onBackToTrain={() => setTrainSubview(current => reduceTrainSubview(current, "open-overview"))}
                  onReanalyzeWithCurrentFtp={() => void runAnalysis()}
                  activitySession={activitySession}
                  unifiedAnalysis={unifiedAnalysis}
                  personalBaseline={baselineResult}
                  contextEvents={athleteTimelineEvents}
                  onOpenRelatedActivityFromContext={activityId => void openHistoryAnalysis(activityId)}
                  onOpenDailyHealthDateFromContext={localDate => {
                    setRecoverySubview("daily-health");
                    setSelectedDailyHealthDate(localDate);
                    selectPage("recovery");
                  }}
                  restoredAnalysisMessage={restoredAnalysisMessage}
                />
              )}
            </TrainWorkflowErrorBoundary>
          </Page>
        )}

        {active === "nutrition" && (
          <Page title="Nutrition" eyebrow="PERFORMANCE FUELING">
            <NutritionIntelligence />
          </Page>
        )}

        {active === "recovery" && (
          <Page title="Recovery" eyebrow="READINESS MANAGEMENT">
            <RecoveryPlan />
            <Card>
              <DailyHealthPanel
                connector={appleHealthConnector}
                records={dailyHealthRecords}
                sessions={healthImportSessions}
                diagnostics={dailyHealthDiagnostics}
                preferredUnits={athleteProfile.personal.preferredUnits}
                onImportCompleted={result => {
                  void handleDailyHealthImportCompleted(result).then(() => void refreshDailyHealthState());
                }}
                onImportError={handleDailyHealthImportError}
                selectedDate={selectedDailyHealthDate}
                onSelectedDateChange={localDate => {
                  setRecoverySubview("daily-health");
                  setSelectedDailyHealthDate(localDate);
                }}
              />
            </Card>
          </Page>
        )}

        {active === "equipment" && (
          <Page title="Garage" eyebrow="EQUIPMENT">
            <GaragePage />
          </Page>
        )}

        {active === "intelligence" && (
          <Page title="Intelligence" eyebrow="DECISION SUPPORT">
            <IntelligencePage
              athleteIntelligence={athleteIntelligence}
              dailyHealthRecords={indexedDailyHealth}
              historySummary={{
                rideCount: athleteHistoryIndex.getRideCount(),
                rideDateRange: historyDateRange.rides,
                dailyHealthCount: athleteHistoryIndex.getHealthRecordCount(),
                dailyHealthDateRange: historyDateRange.dailyHealth
              }}
              preferredUnits={athleteProfile.personal.preferredUnits}
            />
          </Page>
        )}

        {active === "settings" && (
          <Page title="Athlete Settings" eyebrow="PROFILE">
            <AthleteProfileSettingsPage />
          </Page>
        )}

        {import.meta.env.DEV ? (
          <details className="upload-diagnostics" style={{ marginTop: 12 }}>
            <summary>Navigation diagnostics</summary>
            <dl>
              <div><dt>Last active page</dt><dd>{navigationDiagnostics.lastActivePage}</dd></div>
              <div><dt>Restored page</dt><dd>{navigationDiagnostics.restoredPage}</dd></div>
              <div><dt>Restoration source</dt><dd>{navigationDiagnostics.restorationSource}</dd></div>
              <div><dt>Fallback reason</dt><dd>{navigationDiagnostics.fallbackReason ?? "None"}</dd></div>
              <div><dt>Active subview</dt><dd>{navigationDiagnostics.activeSubview}</dd></div>
              <div><dt>Explicit URL route precedence</dt><dd>{navigationDiagnostics.explicitUrlRouteTookPrecedence ? "yes" : "no"}</dd></div>
            </dl>
          </details>
        ) : null}

      </main>
    </div>
  );
}

function Page({ title, eyebrow, children }: {title: string; eyebrow: string; children: React.ReactNode}) {
  return <><div className="page-heading"><p className="eyebrow">{eyebrow}</p><h1>{title}</h1></div>{children}</>;
}

const rootElement = document.getElementById("root");
if (rootElement) {
  tracePersistence("APP START");
  createRoot(rootElement).render(
    <React.StrictMode>
      <AthleteProfileProvider>
        <App />
      </AthleteProfileProvider>
    </React.StrictMode>
  );
}
